<template>
    <div class="wedding-rings">
        <PageContentLoader v-if="(fetching || stale) && !data" />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :loading="fetching"
                @click="executeQuery"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <div v-else-if="data">
            <ProductBreadcrumb :breadcrumbs="breadcrumbs" />

            <div
                v-qa="'before-filters-content'"
                class="before-filters-content"
            >
                <ThTitle
                    v-if="facetedTitle"
                    alignment="CENTER"
                    color="var(--blue)"
                    tag="H1"
                >
                    {{ facetedTitle }}
                </ThTitle>

                <CmsBlocks
                    v-else
                    :data="data.pageProductListing?.beforeFilters || []"
                />
            </div>

            <NameSearch v-model="search" />

            <ProductGrid
                :active-filter-count
                :banner1="data.pageProductListing?.banner1 || undefined"
                :banner2="data.pageProductListing?.banner2 || undefined"
                :data="data.catalogueWeddingRings"
                :fetching
                :full-grid-at="perPage"
                :get-route-sort="filterRoutes.getRouteSort"
                :page
                :results-count="data.catalogueWeddingRingsAggregate.count"
                :filter-chips
                :route-reset="filterRoutes.routeReset"
                :sort-by="sortByWithDefault"
                :track-clicked
                @open-filters-modal="isFiltersModalOpen = true"
            />

            <Pagination
                v-if="data.catalogueWeddingRingsAggregate.count > 0"
                :count="data.catalogueWeddingRingsAggregate.count"
                :current="page"
                :get-route-page="filterRoutes.getRoutePage"
                :limit="perPage"
                :range="2"
                :response
                :scroll-into-view-offset="{ top: 120, left: 0 }"
                scroll-into-view-selector=".product-grid"
            />

            <FacetedContent
                v-if="facetedContent"
                :content="facetedContent"
            />

            <CmsBlocks
                v-else
                :data="data.pageProductListing?.afterGrid || []"
            />
        </div>

        <WeddingRingFilters
            v-if="data && filters && !error && isActivated"
            v-model:values="filterInput"
            v-qa="'wedding-ring-filters'"
            :disabled="fetching"
            :filters="filters"
            :is-open="isFiltersModalOpen"
            :chips="filterChips"
            :result-count="data.catalogueWeddingRingsAggregate.count"
            :filter-routes
            :loading="fetching"
            @close="closeFilterModal"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, type ComputedRef, toRef } from 'vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'

import {
    CatalogueSortOrder,
    type MetalCode,
    type ProductPageFacetedContentInput,
    useWeddingRingBrowseQuery,
    type WeddingRingBrowseQueryVariables,
    type WeddingRingCategoryLabelCode,
    type WeddingRingStyleLabelCode,
} from '@/graphql/api'

import { useTranslator } from '@/i18n'
import { useRegionPlugin } from '@/plugins/region'
import { useResponseError } from '@/helpers/errors'
import { useMeta } from '@/composables/app/useMeta'
import { useFacetedContent } from '@/domain/product/useFacetedContent'
import { useWeddingRingsFilters } from './useWeddingRingsFilters'
import { useProductListingPage } from '@/views/product/useProductListingPage'
import { useRoutes } from '@/router/routes'
import { getWeddingRingBreadcrumbs } from '@/views/product/wedding-ring/getWeddingRingBreadcrumbs.ts'
import { useActivated } from '@/composables/useActivated'
import { useProductListAnalytics } from '../composables/analytics/useProductListAnalytics'

import WeddingRingFilters from '@/components/rings/wedding/WeddingRingFilters.vue'
import ProductBreadcrumb from '@/components/rings/ProductBreadcrumb.vue'
import Pagination from '@/components/ui/Pagination.vue'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import ProductGrid from '@/components/rings/product-grid/ProductGrid.vue'
import FacetedContent from '../../../components/product/FacetedContent.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import NameSearch from '@/components/rings/NameSearch.vue'
import { useRouter } from 'vue-router'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

import { useRoutePathWatcher } from '@/composables/route-path-watcher.ts'
import { usePageCount } from '@/composables/page-count.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'

export type WeddingRingsProps = {
    slug: string[]
    category?: WeddingRingCategoryLabelCode
    metal?: MetalCode
    ringStyle?: WeddingRingStyleLabelCode
    page: number
    priceMin?: number
    priceMax?: number
    sortBy?: CatalogueSortOrder
}

const props = defineProps<WeddingRingsProps>()

const perPage = 32

const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const currency = useCurrencyInput()
const { t } = useI18n()
const routes = useRoutes()
const router = useRouter()

const pathWatcher = useRoutePathWatcher(
    () => {
        return router.resolve(
            routes.weddingRings({
                category: props.category,
                metal: props.metal,
                ringStyle: props.ringStyle,
                page: props.page,
            })
        )
    },
    () => {
        return router.resolve(
            routes.weddingRings({
                category: props.category,
                metal: props.metal,
                ringStyle: props.ringStyle,
                priceMin: props.priceMin,
                priceMax: props.priceMax,
                page: props.page,
                sortBy: props.sortBy,
            })
        ).fullPath
    },
    301
)

const {
    filterRoutes,
    filterInput,
    activeFilterCount,
    filterChips,
    isFiltersModalOpen,
    closeFilterModal,
    search,
} = useWeddingRingsFilters(
    props,
    toRef(() => data.value?.catalogueWeddingRingFilters)
)

const facetedContentInput = computed(() => ({
    weddingRings: {
        metal: props.metal,
        category: props.category,
        ringStyle: props.ringStyle,
    },
})) satisfies ComputedRef<ProductPageFacetedContentInput>

const activeFacetedFilterCount = computed(
    () =>
        Object.values(facetedContentInput.value.weddingRings).filter(Boolean)
            .length
)

const sortByWithDefault = toRef(
    () => props.sortBy ?? CatalogueSortOrder.Recommended
)

const variables = computed(
    (): WeddingRingBrowseQueryVariables => ({
        filter: filterInput.value,
        currency: currency.value,
        region: regionCode.value,
        page: props.page,
        limit: perPage,
        locale: locale.value,
        path: 'wedding-rings',
        facetedContentInput: facetedContentInput.value,
        sortBy: sortByWithDefault.value,
    })
)

const response = useWeddingRingBrowseQuery({ variables })

const pageCount = usePageCount(
    () => response.data.value?.catalogueWeddingRingsAggregate.count ?? 0,
    () => perPage
)
const pageWatcher = usePageWatcher(
    () => props.page,
    pageCount,
    () => {
        return router.resolve(filterRoutes.getRoutePage(1)).fullPath
    },
    302
)

await response

const responseErrors = useResponseError(response)

const { stale, fetching, data, error, executeQuery } = response

const catalogue = toRef(() => data.value?.catalogueWeddingRings)

const content = toRef(() => data.value?.pageProductListingFacetedContent)

const filters = toRef(() => data.value?.catalogueWeddingRingFilters)

const {
    facetedContent,
    facetedTitle,
    facetedMetaDescription,
    facetedMetaTitle,
} = useFacetedContent(
    toRef(() => ({
        type: 'wedding-rings' as const,
        filters: filters.value,
        filterValues: filterInput.value,
    })),
    content,
    activeFacetedFilterCount,
    toRef(() => data.value?.pageProductListing?.meta)
)

const facets = computed(() => ({
    category: filters.value?.categories.find(
        (category) => category.label.systemCode === props.category
    ),
    metal: filters.value?.primaryMetals.find(
        (metal) => metal.metal.systemCode === props.metal
    ),
    ringStyle: filters.value?.ringStyles.find(
        (ringStyle) => ringStyle.label.systemCode === props.ringStyle
    ),
}))

const facetCategory = toRef(() => facets.value.category?.label)
const facetMetal = toRef(() => facets.value.metal?.metal)
const facetRingStyle = toRef(() => facets.value.ringStyle?.label)

const { trackClicked } = useProductListAnalytics(
    catalogue,
    filterInput,
    computed(() => [
        facetCategory.value?.translation.name,
        facetMetal.value?.translation.name,
        facetRingStyle.value?.translation.name,
    ]),
    sortByWithDefault,
    toRef(() => props.page)
)

const { breadcrumbs } = useProductListingPage({
    facets,
    resetRouteGetter: (facets) =>
        routes.weddingRings({
            category: facets.category?.label.systemCode,
            metal: facets.metal?.metal.systemCode,
            ringStyle: facets.ringStyle?.label.systemCode,
        }),
    breadcrumbsGetter: () =>
        getWeddingRingBreadcrumbs(
            {
                category: facetCategory,
                metal: facetMetal,
                ringStyle: facetRingStyle,
                product: undefined,
            },
            routes
        ),
})

const { isActivated } = useActivated()

useMeta(
    {
        title: () => facetedMetaTitle.value,
        description: () => facetedMetaDescription.value,
    },
    data,
    fetching,
    stale
)

useHead({
    meta: () => {
        if (activeFacetedFilterCount.value > 2) {
            return [
                {
                    name: 'robots',
                    content: 'noindex, nofollow',
                },
            ]
        }
        return []
    },
})

await pathWatcher()
await pageWatcher()
</script>
